<template>
  <div class="bg-white dark:bg-n-700 rounded-lg shadow-lg border border-n-50 dark:border-n-600 flex flex-col h-full overflow-hidden">
    <div class="flex-shrink-0 bg-n-100 dark:bg-n-600 px-6 py-4 flex items-center justify-between">
      <div>
        <h2 class="font-bold text-2xl text-n-900 dark:text-n-0">{{ t('allday.title') }}</h2>
        <p class="text-sm text-n-500 dark:text-n-400 mt-1">
          {{ t('allday.subtitle', { count: totalItems }) }}
        </p>
      </div>
    </div>

    <div v-if="groupedByCourse.length === 0" class="flex-1 flex items-center justify-center">
      <p class="text-xl text-n-400 dark:text-n-500">{{ t('summary.no-products') }}</p>
    </div>

    <div v-else class="flex-1 overflow-y-auto">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
        <div
          v-for="group in groupedByCourse"
          :key="group.course"
          class="border-b border-r border-n-100 dark:border-n-600"
        >
          <div class="bg-n-50 dark:bg-n-800 px-4 py-2">
            <h3 class="font-bold text-sm text-n-600 dark:text-n-300 uppercase tracking-wide">
              {{ group.course || t('allday.uncategorized') }}
            </h3>
          </div>
          <div class="divide-y divide-n-50 dark:divide-n-600">
            <div
              v-for="product in group.products"
              :key="product.key"
              class="flex items-center px-4 py-3 gap-4"
            >
              <div
                class="shrink-0 w-14 h-14 rounded-lg flex items-center justify-center font-bold text-2xl"
                :class="quantityClass(product.totalQuantity)"
              >
                {{ product.totalQuantity }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-lg text-n-900 dark:text-n-0 truncate">
                  {{ product.name }}
                </div>
                <div v-if="product.modifiersSummary" class="text-sm text-n-500 dark:text-n-400 truncate">
                  {{ product.modifiersSummary }}
                </div>
                <div class="text-xs text-n-400 dark:text-n-500 mt-0.5">
                  {{ t('allday.across-orders', { count: product.orderCount }) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { KitchenOrderMapped } from '@/types'

type Props = {
  orders: KitchenOrderMapped[]
}

const props = defineProps<Props>()
const { t } = useI18n()

type GroupedProduct = {
  key: string
  name: string
  course: string
  totalQuantity: number
  orderCount: number
  modifiersSummary: string
}

const groupedByCourse = computed(() => {
  const productMap = new Map<string, GroupedProduct>()

  for (const order of props.orders) {
    const activeProducts = order.mappedProducts.filter(
      p => !p.deleted && p.status !== 'completed' && p.status !== 'cancelled'
    )

    for (const product of activeProducts) {
      const modifiersSummary = product.modifiers
        ?.map(m => (m.quantity > 1 ? `${m.quantity}x ${m.name}` : m.name))
        .join(', ') || ''
      const modifiersKey = product.modifiers
        ?.map(m => `${m.id}-${m.quantity}`)
        .sort()
        .join('|')
      const key = `${product.name}|${product.course || ''}|${modifiersKey || ''}`

      const existing = productMap.get(key)
      if (existing) {
        existing.totalQuantity += product.quantity
        existing.orderCount += 1
      } else {
        productMap.set(key, {
          key,
          name: product.name,
          course: product.course || '',
          totalQuantity: product.quantity,
          orderCount: 1,
          modifiersSummary
        })
      }
    }
  }

  const courseMap = new Map<string, GroupedProduct[]>()
  for (const product of productMap.values()) {
    const course = product.course
    if (!courseMap.has(course)) {
      courseMap.set(course, [])
    }
    courseMap.get(course)!.push(product)
  }

  // Sort products within each course by quantity descending
  for (const products of courseMap.values()) {
    products.sort((a, b) => b.totalQuantity - a.totalQuantity)
  }

  return Array.from(courseMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([course, products]) => ({ course, products }))
})

const totalItems = computed(() =>
  groupedByCourse.value.reduce(
    (sum, group) => sum + group.products.reduce((s, p) => s + p.totalQuantity, 0),
    0
  )
)

function quantityClass(qty: number) {
  if (qty >= 10) return 'bg-r-100 dark:bg-r-900/40 text-r-dark dark:text-r-400'
  if (qty >= 5) return 'bg-y-100 dark:bg-y-900/40 text-y-550 dark:text-y-400'
  return 'bg-n-100 dark:bg-n-600 text-n-900 dark:text-n-0'
}
</script>
